import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Grid, Button } from "@mui/material";
import PatientLayout from "../../../layouts/Patient";
import {
  ColorectalSurgeryPostoperativeForm as ColorectalSurgeryPostoperativeFormConstant,
  ColorectalSurgeryPostoperativeListForm as ColorectalSurgeryPostoperativeListFormConstant,
} from "../../../constants/Form";
import { Colors as ColorsConstant } from "../../../constants/themes/Colors";
import { Paths as PathsConstant } from "../../../constants/Route";
import { useStores } from "../../../stores";
import { CreatePostoperative as CreatePostoperativeService } from "../../../services/colorectals/Postoperative";
import FormPostoperative from "../../../components/colorectalSurgeries/postoperatives/Form";
import LoadingWholeScreen from "../../../components/loading/WholeScreen";
import ModalFail from "../../../components/modals/Fail";

interface Props {}

const Create = observer((props: Props) => {
  const navigate = useNavigate();
  const params = useParams();
  const { PostoperativeStore } = useStores();
  const [isLoading, setIsLoading] = useState(false);
  const [isShowFailModal, setIsShowFailModal] = useState(false);

  const useQuery = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  };
  const query = useQuery();

  useEffect(() => {
    PostoperativeStore.onSetIsReadOnly(false);
  }, [PostoperativeStore]);

  const onNavigateToColorectalSearchPatient = () => {
    const { patientId } = params;
    query.get("dashboard")
      ? navigate(`${PathsConstant.colorectalSurgeryDashboard}/${patientId}`)
      : navigate(
          `${PathsConstant.colorectalSurgeryPostOperativeSearchPatient}?formType=preoperative`
        );
  };
  const onCreatePostoperative = async () => {
    setIsLoading(true);
    const { patientId } = params;
    const formIsValid = PostoperativeStore.onValidateForm();
    if (formIsValid) {
      const body = PostoperativeStore.onGetPostoperativeBody();
      const response = await CreatePostoperativeService(patientId || "", body);
      if (response.success) {
        query.get("dashboard")
          ? navigate(`${PathsConstant.colorectalSurgeryDashboard}/${patientId}`)
          : navigate(PathsConstant.colorectalSurgeryPostOperativeList);
      } else setIsShowFailModal(true);
    }
    setIsLoading(false);
  };

  return (
    <PatientLayout
      formSectionList={ColorectalSurgeryPostoperativeListFormConstant}
    >
      <>
        {isLoading && <LoadingWholeScreen isShowDescription={true} />}
        <Box
          id={ColorectalSurgeryPostoperativeFormConstant.newPostOperative}
          paddingBottom={1}
          borderBottom="1px solid"
          borderColor={ColorsConstant.border}
          color={ColorsConstant.black}
        >
          <Typography variant="h6">New postoperative</Typography>
        </Box>
        <Box marginTop={2}>
          <FormPostoperative />
        </Box>
        <Grid
          container
          spacing={2}
          marginTop={4}
          marginBottom={4}
          justifyContent="center"
          direction={{ xs: "column-reverse", md: "row" }}
        >
          <Grid item xs={12} sm={10} md={4} lg={3} xl={2}>
            <Button
              variant="outlined"
              onClick={onNavigateToColorectalSearchPatient}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={12} sm={10} md={4} lg={3} xl={2}>
            <Button variant="contained" onClick={onCreatePostoperative}>
              Create
            </Button>
          </Grid>
        </Grid>
        <ModalFail
          isOpen={isShowFailModal}
          title="Create postoperative"
          description="Failed to create new postoperative. Please try again."
          onClose={() => {
            setIsShowFailModal(false);
          }}
        />
      </>
    </PatientLayout>
  );
});

export default Create;
